<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import { ArrowRight } from 'lucide-vue-next';
import { computed } from 'vue';
import AppLogoIcon from '@/components/AppLogoIcon.vue';
import MarketingFooter from '@/components/marketing/MarketingFooter.vue';
import { dashboard, login, register } from '@/routes';
import type { Team } from '@/types';

const page = usePage<{ currentTeam?: Team | null }>();
const currentTeam = computed(() => page.props.currentTeam ?? null);
</script>

<template>
    <div class="relative min-h-screen overflow-x-clip bg-white text-foreground dark:bg-zinc-950">
        <header class="sticky top-0 z-40 border-b border-zinc-200 bg-white/95 shadow-sm backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-950/95">
            <div class="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-3.5">
                <Link href="/" class="flex min-w-0 items-center gap-2 sm:gap-2.5">
                    <AppLogoIcon uid="marketing-nav" class="size-7 flex-none sm:size-8" />
                    <span class="font-display truncate text-base font-bold tracking-tight sm:text-lg">CreativeTint</span>
                </Link>

                <div class="flex items-center gap-1.5 sm:gap-2">
                    <Link
                        v-if="currentTeam"
                        :href="dashboard(currentTeam.slug)"
                        class="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-sm font-semibold text-background transition hover:opacity-90 sm:px-4"
                    >
                        <span class="hidden sm:inline">Open dashboard</span>
                        <span class="sm:hidden">Dashboard</span>
                        <ArrowRight class="size-4" />
                    </Link>
                    <template v-else>
                        <Link
                            :href="login()"
                            class="hidden rounded-lg px-3 py-2 text-sm font-semibold text-zinc-800 transition hover:text-violet-600 dark:text-zinc-100 dark:hover:text-violet-300 sm:inline"
                        >
                            Log in
                        </Link>
                        <Link
                            :href="register()"
                            class="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-sm font-semibold text-background transition hover:opacity-90 sm:px-4"
                        >
                            Get started
                            <ArrowRight class="size-4" />
                        </Link>
                    </template>
                </div>
            </div>
        </header>

        <main class="relative">
            <slot />
        </main>

        <MarketingFooter />
    </div>
</template>
